/**
 * ErrorDetailsAccordion component
 * 
 * Reusable accordion component that displays technical error details
 * and provides one-click copy functionality for support tickets.
 * 
 * Usage: Add within error Alert/Card components to provide expandable
 * technical details below user-friendly error messages.
 */

import React, {useState} from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Typography,
    Box
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import {AppError} from '../../types/errors';
import {formatErrorDetails, copyErrorDetails} from '../../utils/errorFormatting';
import {showToastAlert} from '../../functions/alerts';

interface ErrorDetailsAccordionProps {
    error: AppError;
}

/**
 * Displays collapsible technical error details with copy button
 * 
 * @param error - AppError object with all error information
 */
export const ErrorDetailsAccordion: React.FC<ErrorDetailsAccordionProps> = ({error}) => {
    const [expanded, setExpanded] = useState(false);

    const handleCopy = async () => {
        const success = await copyErrorDetails(error);
        if (success) {
            showToastAlert('Error details copied to clipboard', 'success');
        } else {
            showToastAlert('Failed to copy - please select and copy manually', 'error');
        }
    };

    return (
        <Accordion 
            expanded={expanded} 
            onChange={() => setExpanded(!expanded)} 
            sx={{mt: 1}}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2">Technical Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Box sx={{mb: 2}}>
                    <Typography 
                        variant="body2" 
                        component="pre" 
                        sx={{
                            whiteSpace: 'pre-wrap', 
                            fontFamily: 'monospace',
                            fontSize: '0.875rem'
                        }}
                    >
                        {formatErrorDetails(error)}
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopy}
                >
                    Copy Error Details
                </Button>
            </AccordionDetails>
        </Accordion>
    );
};
